import { useMutation, useQuery, useQueryClient } from "react-query";

import api from "../../utils/api";
import { invalidate } from "../../helpers/clientHelper";
import { useHubSnackBar } from "../useSnackBar";

const LEADS = "/sales/leads";
const QUOTES = "/sales/quotes";

// ── Optimistic stage-move helper (shared by leads + quotes kanbans) ──
const optimisticStageMove = async (queryClient, key, { id, stage }) => {
  await queryClient.cancelQueries(key);
  const previous = queryClient.getQueryData(key);
  if (Array.isArray(previous)) {
    const updated = previous.map((col) => ({
      ...col,
      elements: col.elements.filter((e) => String(e.id) !== String(id)),
    }));
    const source = previous.find((col) =>
      col.elements.some((e) => String(e.id) === String(id)),
    );
    const moved = source?.elements.find((e) => String(e.id) === String(id));
    const target = updated.find((col) => String(col.id) === String(stage));
    if (target && moved) target.elements.push({ ...moved, stage });
    queryClient.setQueryData(key, updated);
  }
  return { previous };
};

// ── Leads ──

const getLeads = () => api.get(LEADS);
export const useGetLeads = () => useQuery([LEADS], getLeads);

const getLeadDetails = ({ queryKey }) => api.get(`${LEADS}/${queryKey[1]}`);
export const useGetLeadDetails = (id) =>
  useQuery([LEADS, id], getLeadDetails, { enabled: !!id });

const getLeadHistory = ({ queryKey }) =>
  api.get(`${LEADS}/${queryKey[1]}/history`);
export const useGetLeadHistory = (id) =>
  useQuery([LEADS, id, "history"], getLeadHistory, { enabled: !!id });

const postLead = (data) => api.post(LEADS, data);
export const usePostLead = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(postLead, {
    onSuccess: () => {
      invalidate(queryClient, LEADS);
      addSnackbarSuccess({ message: "Lead created" });
    },
  });
};

const putLead = ({ id, ...data }) => api.put(`${LEADS}/${id}`, data);
export const usePutLead = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(putLead, {
    onSuccess: () => {
      invalidate(queryClient, LEADS);
      addSnackbarSuccess({ message: "Lead updated" });
    },
  });
};

const putLeadStage = ({ id, stage }) =>
  api.put(`${LEADS}/${id}/stage`, { stage });
export const usePutLeadStage = () => {
  const { addSnackbarError } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(putLeadStage, {
    onMutate: (vars) => optimisticStageMove(queryClient, LEADS, vars),
    onError: (_err, _vars, ctx) => {
      ctx?.previous && queryClient.setQueryData(LEADS, ctx.previous);
      addSnackbarError({ message: "Failed to move lead" });
    },
    onSettled: () => invalidate(queryClient, LEADS),
  });
};

const deleteLead = (id) => api.delete(`${LEADS}/${id}`);
export const useDeleteLead = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(deleteLead, {
    onSuccess: () => {
      invalidate(queryClient, LEADS);
      addSnackbarSuccess({ message: "Lead deleted" });
    },
  });
};

// ── Quotes ──

const getQuotes = () => api.get(QUOTES);
export const useGetQuotes = () => useQuery([QUOTES], getQuotes);

const getCustomerQuotes = ({ queryKey }) =>
  api.get(`${QUOTES}/customer/${queryKey[2]}`);
export const useGetCustomerQuotes = (customerId) =>
  useQuery([QUOTES, "customer", customerId], getCustomerQuotes, {
    enabled: !!customerId,
  });

const getPartQuotes = ({ queryKey }) =>
  api.get(`${QUOTES}/part/${queryKey[2]}`);
export const useGetPartQuotes = (partId, enabled = true) =>
  useQuery([QUOTES, "part", partId], getPartQuotes, {
    enabled: !!partId && enabled,
  });

const getQuoteDetails = ({ queryKey }) => api.get(`${QUOTES}/${queryKey[1]}`);
export const useGetQuoteDetails = (id) =>
  useQuery([QUOTES, id], getQuoteDetails, { enabled: !!id });

const getQuoteHistory = ({ queryKey }) =>
  api.get(`${QUOTES}/${queryKey[1]}/history`);
export const useGetQuoteHistory = (id) =>
  useQuery([QUOTES, id, "history"], getQuoteHistory, { enabled: !!id });

const getQuoteByNumber = ({ queryKey }) =>
  api.get(`${QUOTES}/number/${queryKey[2]}`);
export const useGetQuoteByNumber = (quoteNumber) =>
  useQuery([QUOTES, "number", quoteNumber], getQuoteByNumber, {
    enabled: !!quoteNumber,
  });

const postQuote = (data) => api.post(QUOTES, data);
export const usePostQuote = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(postQuote, {
    onSuccess: () => {
      invalidate(queryClient, QUOTES);
      addSnackbarSuccess({ message: "Quote created" });
    },
  });
};

const putQuote = ({ id, ...data }) => api.put(`${QUOTES}/${id}`, data);
export const usePutQuote = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(putQuote, {
    onSuccess: () => {
      invalidate(queryClient, QUOTES);
      addSnackbarSuccess({ message: "Quote updated" });
    },
  });
};

const putQuoteStage = ({ id, stage }) =>
  api.put(`${QUOTES}/${id}/stage`, { stage });
export const usePutQuoteStage = () => {
  const { addSnackbarError } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(putQuoteStage, {
    onMutate: (vars) => optimisticStageMove(queryClient, QUOTES, vars),
    onError: (_err, _vars, ctx) => {
      ctx?.previous && queryClient.setQueryData(QUOTES, ctx.previous);
      addSnackbarError({ message: "Failed to move quote" });
    },
    onSettled: () => invalidate(queryClient, QUOTES),
  });
};

const deleteQuote = (id) => api.delete(`${QUOTES}/${id}`);
export const useDeleteQuote = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(deleteQuote, {
    onSuccess: () => {
      invalidate(queryClient, QUOTES);
      addSnackbarSuccess({ message: "Quote deleted" });
    },
  });
};

// Send a rendered Costing Sheet PDF to Drive. Server uploads it to
//   <attachments root>/<Letter>/<Customer>/Quotes/<PartNumber>/Costing Sheet - <quoteNumber>.pdf
// and persists the resulting webViewLink on the quote.
const postQuoteCostingSheet = ({ id, file }) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`${QUOTES}/${id}/costing-sheet`, formData);
};
export const usePostQuoteCostingSheet = () => {
  const { addSnackbarSuccess, addSnackbarError } = useHubSnackBar();
  const queryClient = useQueryClient();
  return useMutation(postQuoteCostingSheet, {
    onSuccess: () => {
      invalidate(queryClient, QUOTES);
      addSnackbarSuccess({ message: "Costing sheet sent to Drive" });
    },
    onError: (error) => addSnackbarError({ error }),
  });
};
